import React, { useContext } from 'react';
import { AppContext } from '../../components/app/AppProvider.component';
import {
  RecommendationAttemptDto,
  RecommendationAttemptStatusDto,
  RecommendationDto,
  RecommendationContentTypeDto,
} from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import {
  ActivityTypeConfigMap,
  RemediationContentTypeConfigMap
} from '../../models/remediation.models';
import { AppLinkData } from '../../apis/eols-app-link/eols-app-link.dtos';
import {
  getAssessmentGoalFromRecommendation,
  getAssessmentGoalTotalsCombined,
  getAssessmentSubmissionByAttempt,
  getCategoryGroupNameForGoal,
  getContentTypePassNumber,
  getGoalBreadcrumbs,
  getMetricCounts,
  getQuizSessionResults,
  getTotalProgress,
  isGoalComplete,
  isTotalPLPComplete
} from '../../utilities/remediation.utilities';
import { RecordObject } from '../../models/common.models';

export type RecommendationPostPlayerMessageProps = {
  recommendation: RecommendationDto;
  attempt: RecommendationAttemptDto;
  appLinkData: AppLinkData;
};

const ReturnMessage = (props: {
  attempt: RecommendationAttemptDto;
  recommendation: RecommendationDto;
}) => {

  const { appState } = useContext(AppContext);
  const baseState = appState.remediationBaseState;

  const {
    attempt,
    recommendation
  } = props;

  const submission = getAssessmentSubmissionByAttempt(attempt, baseState.attemptAssessmentSubmissions);
  const assessmentGoal = getAssessmentGoalFromRecommendation(baseState.eolsAssessment, recommendation);
  const contentTypeDisplay = RemediationContentTypeConfigMap[recommendation.contentType].displayName.toLowerCase();
  const scoreToPass = getContentTypePassNumber(recommendation, baseState);

  const goalStatusCounts = getAssessmentGoalTotalsCombined({
    assessmentGoal,
    baseState,
  });

  const activityTypeMetricCounts = getMetricCounts(
    baseState,
    recommendation.assessmentGoalType
  );

  const parts = [];

  if (!submission) {
    parts.push((
      <div>
        You closed your {contentTypeDisplay} before completing it,
        you can resume this {contentTypeDisplay} by using the option below or choose to resume it at a different time.
      </div>
    ));
  } else if (submission.score < scoreToPass) {
    parts.push((
      <div>
        Nice try! You completed this {contentTypeDisplay} scoring <strong>{Math.round(submission.score * 100)}%</strong>.
        Unfortunately, you are required to score <strong>{Math.round(scoreToPass * 100)}%</strong> to receive credit.
        You can choose to reattempt using the option below or reattempt this {contentTypeDisplay} at a different time.
      </div>
    ));
  } else if (RemediationContentTypeConfigMap[recommendation.contentType].isScored) {
    parts.push((
      <div>
        Great job! You completed this {contentTypeDisplay} scoring <strong>{Math.round(submission.score * 100)}%</strong>.
      </div>
    ));
  } else {
    parts.push((
      <div>
        Great job! You completed this {contentTypeDisplay}.
      </div>
    ));
  }

  if (isTotalPLPComplete(baseState)) {
    parts.push((
      <div>
        You may review any additional content if you would like.
      </div>
    ));
  } else if (isGoalComplete(activityTypeMetricCounts)) {
    parts.push((
      <div>
        All of your required work for the {ActivityTypeConfigMap[recommendation.assessmentGoalType].title.toLowerCase()} section is complete!
      </div>
    ));
  } else if (isGoalComplete(goalStatusCounts)) {
    parts.push((
      <div>
        All of your required work for {getGoalBreadcrumbs(assessmentGoal, getCategoryGroupNameForGoal(assessmentGoal, baseState)).text} is complete!
      </div>
    ));
  }

  if (parts.length === 1) {
    return <div className="">{parts[0]}</div>;
  }

  return (
    <>
      <div className="">{parts[0]}</div>
      <div className="">{parts[1]}</div>
    </>
  );
};

const QuizReturnMessage = (props: {
  attempt: RecommendationAttemptDto;
  quizSessionResults: {
    totalQuestions: number;
    totalCorrect: number;
  };
  recommendation: RecommendationDto;
}) => {

  const { appState } = useContext(AppContext);
  const baseState = appState.remediationBaseState;

  const {
    attempt,
    quizSessionResults,
    recommendation,
  } = props;

  const progress = getTotalProgress(baseState);

  if (!attempt) {
    return null;
  }

  const parts = [];

  if (!quizSessionResults || quizSessionResults.totalQuestions === 0) {
    parts.push((
      <div>
        You closed the quiz before answering any questions.
      </div>
    ));
  } else if (quizSessionResults.totalCorrect === 0) {
    parts.push((
      <div>
        Nice try! You attempted {quizSessionResults.totalQuestions} question(s) but unfortunately did not get any correct.
        You will be able to reattempt these incorrect questions once you have attempted all quiz questions.
      </div>
    ));
  } else {
    parts.push((
      <div>
        Great job! you completed <strong>{quizSessionResults.totalQuestions} questions</strong> and
        got <strong>{quizSessionResults.totalCorrect} correct</strong>.
      </div>
    ));
  }

  if (progress >= 100) {
    parts.push((
      <div>
        You may review any additional content if you would like.
      </div>
    ));
  } else if (attempt.status === RecommendationAttemptStatusDto.COMPLETED_PASSED) {
    parts.push((
      <div>
        <div>
          All of your required work for the {ActivityTypeConfigMap[recommendation.assessmentGoalType].title.toLowerCase()} section is complete!
        </div>
        <div>
          You can continue to practice these quiz questions or continue to the next recommendation.
        </div>
      </div>
    ));
  } else {
    parts.push((
      <div>
        You may continue your quizzing now or at a later time.
      </div>
    ));
  }

  if (parts.length === 1) {
    return <div className="">{parts[0]}</div>;
  }

  return (
    <>
      <div className="">{parts[0]}</div>
      <div className="">{parts[1]}</div>
    </>
  );
};

export const RecommendationPostPlayerMessage = (props: RecommendationPostPlayerMessageProps): JSX.Element => {
  const { appState } = useContext(AppContext);
  const baseState = appState.remediationBaseState;

  const {
    recommendation,
    appLinkData,
    attempt
  } = props;
  if (recommendation.contentType !== RecommendationContentTypeDto.RAAS_QUIZ) {
    return (
      <ReturnMessage
        attempt={attempt}
        recommendation={recommendation}
      />
    );
  }

  const { outPostBody } = appLinkData;
  const quizSessionResults = getQuizSessionResults(
    baseState,
    outPostBody as RecordObject // runtime validated elsewhere; casting to satisfy generic removal of any
  );

  return (
    <QuizReturnMessage
      attempt={attempt}
      quizSessionResults={quizSessionResults}
      recommendation={recommendation}
    />
  );
};

export default RecommendationPostPlayerMessage;
